<template>
    <div>
        <div v-if="keyError || hasSignedContract">
            <h1>This Agreement has been signed.</h1>
        </div>
        <div class="w-full md:w-3/4" v-else>
            <h1 class="mt-2 mb-4 font-sans-bold">{{ (event && event.festival) ? event.festival.title: '' }} Collaborator Agreement</h1>

            <p class="pb-10 mb-10 border-b border-gray-medium">All Reimagine collaborators must sign a collaborator agreement, which includes terms and conditions concerning your rights and responsibilities.  If the collaborator is an organization, you must be authorized to sign on behalf of the organization.  If you are not authorized, you may send an email below to a representative of the organization who is authorized to sign.</p>

            <div class="w-full my-3 overflow-y-scroll mb-12 contract-text" v-html="contractLegal"></div>

            <div class="mb-10">
                <p>By completing the information below, I acknowledge and agree: (A) I am electronically signing this Agreement and I have read, understood, and agree to the terms of this Agreement and Reimagine’s website <a href="https://www.letsreimagine.org/terms-conditions/" target="_blank">Terms &amp; Conditions</a> and <a href="https://www.letsreimagine.org/privacy-cookies/" target="_blank">Privacy Policy</a>; and (B) if I am signing on behalf of an organization, I warrant that I am authorized by the organization to sign on its behalf and to bind it to the terms of this Agreement.</p>
            </div>

            <div class="mb-12">
                <label for="signDate" ref="signDate">
                    Date <span class="text-red">*</span>
                </label>

                <datepicker id="signDate" name="signDate"
                    v-validate="'required'"
                    :format="'yyyy-MM-dd'"
                    v-model="form.signDate">
                </datepicker>
                <span v-show="errors.has('signDate')" class="error text-error-red text-lg">{{ errors.first('signDate') }}</span>
            </div>

            <div class="mb-12">
                <label for="name" ref="name">
                    Your Name <span class="text-red">*</span>
                </label>
                <input id="name" type="text" name="name"
                    v-validate="'required'"
                    v-model="form.name"
                >
                <span v-show="errors.has('name')" class="error text-error-red text-lg">{{ errors.first('name') }}</span>
            </div>

            <div class="mb-12">
                <label for="organizationName" ref="organizationName">
                    Organization Name (if applicable)
                </label>
                <input id="organizationName" type="text" name="organizationName"
                    v-model="form.organizationName"
                >
                <span v-show="errors.has('organizationName')" class="error text-error-red text-lg">{{ errors.first('organizationName') }}</span>
            </div>

            <div class="mb-12">
                <label for="title" ref="title">
                    Your Position/Title at Organization (if applicable)
                </label>
                <input id="title" type="text" name="title"
                    v-model="form.title"
                >
                <span v-show="errors.has('title')" class="error text-error-red text-lg">{{ errors.first('title') }}</span>
            </div>

            <div class="mb-12">
                <label for="address_1" ref="address_1">
                    Address <span class="text-red">*</span>
                </label>
                <input id="address_1" type="text" name="address_1"
                    v-validate="'required'"
                    v-model="form.address_1"
                >
                <span v-show="errors.has('address_1')" class="error text-error-red text-lg">{{ errors.first('address_1') }}</span>
            </div>

            <div class="mb-12 flex flex-wrap">
                <div class="w-full md:w-1/3 md:pr-2 mb-12 md:mb-0">
                    <label for="city" ref="city">
                        City <span class="text-red">*</span>
                    </label>
                    <input id="city" type="text" name="city"
                        v-validate="'required'"
                        v-model="form.city"
                    >
                    <span v-show="errors.has('city')" class="error text-error-red text-lg">{{ errors.first('city') }}</span>
                </div>
                <div class="w-full md:w-1/3 md:pr-2 mb-12 md:mb-0">
                    <label for="state" ref="state">
                        State <span class="text-red">*</span>
                    </label>
                    <input id="state" type="text" name="state"
                        v-validate="'required'"
                        v-model="form.state"
                    >
                    <span v-show="errors.has('state')" class="error text-error-red text-lg">{{ errors.first('state') }}</span>
                </div>
                <div class="w-full md:w-1/3">
                    <label for="zip" ref="zip">
                        Zip <span class="text-red">*</span>
                    </label>
                    <input id="zip" type="text" name="zip"
                        v-validate="'required'"
                        v-model="form.zip"
                    >
                    <span v-show="errors.has('zip')" class="error text-error-red text-lg">{{ errors.first('zip') }}</span>
                </div>
            </div>

            <div class="mb-12">
                <label for="email" ref="email">
                    Email <span class="text-red">*</span>
                </label>
                <input id="email" type="text" name="email"
                    v-validate="'required|email'"
                    v-model="form.email"
                >
                <span v-show="errors.has('email')" class="error text-error-red text-lg">{{ errors.first('email') }}</span>
            </div>

            <div class="mb-12">
                <label for="phone" ref="phone">
                    Phone <span class="text-red">*</span>
                </label>
                <input id="phone" type="text" name="phone"
                    v-validate="'required'"
                    v-model="form.phone"
                >
                <span v-show="errors.has('phone')" class="error text-error-red text-lg">{{ errors.first('phone') }}</span>
            </div>

            <button @click="agree" class="button mb-12">Agree</button>

            <div v-if="!isByEmail">
                <p class="mb-5">
                    <span class="font-sans-bold">Is someone else the appropriate person to complete this agreement?</span> Email it to them for signature.
                </p>
                <button @click="emailForSignature" class="button button__small mb-12">Email for Signature</button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from "vuex";
    import Datepicker from "vuejs-datepicker";
    import timeHelper from "../../mixins/time-helper";
    import validation from "../../mixins/validation";

    export default {
        name: "ContractSign",
        title: "Sign Contract",
        components: {
            Datepicker
        },
        mixins: [timeHelper, validation],
        data: () => ({
            form: {
                name: "",
                title: "",
                signDate: "",
                organizationName: "",
                address_1: "",
                city: "",
                state: "",
                zip: "",
                email: "",
                phone: "",
                eventId: null,
                festivalId: null,
                organizationId: null,
                contractId: null,
                eventId: null,
                generateKey: false
            },
            isByEmail: false,
            isAgreeable: false,
            keyError: false,
            hasRecord: false,
            signature: null
        }),
        computed: {
            ...mapGetters({
                event: "event",
                user: "user",
                organization: "organization",
                contract: "contract"
            }),
            hasSignedContract() {
                return (this.user && this.user.hasSignedContract) ? true : false
            },
            contractLegal() {
                return this.contract && this.contract.title
                    ? `${this.contract.legal}`
                    : "";
            }
        },
        watch: {
            user() {
                this.form.organizationId = parseInt(this.user.organization_id);
            },
            event() {
                this.getOrganization();
                this.form.festivalId = parseInt(this.event.festival.id);
            }
        },
        methods: {
            emailForSignature() {
                this.form.generateKey = true;
                let route = this.$laroute.route("api.contracts.signature.store");
                axios
                    .post(route, this.form)
                    .then(response => {
                        const key = response.data.key;
                        this.$router.push({
                            name: "contract-email",
                            params: { key: key }
                        });
                    })
                    .catch(error => {
                        console.log(error);
                        console.log(error.response);
                    });
            },
            getOrganization() {
                this.form.organizationId = this.user.organization_id;
                this.$store.dispatch("getOrganization", {
                    id: this.user.organization_id
                });
            },
            agree() {
                // Via email
                if (this.isByEmail || (this.hasRecord && this.signature.key)) {
                    this.$validator.validateAll().then(result => {
                        if (!result) {
                            this.gotoFirstValidationError();
                            return;
                        }

                        this.form.signDate = this.formatDate(this.form.signDate);
                        this.updateSignature();
                    });

                    // When logged in
                } else {
                    this.$validator.validateAll().then(result => {
                        if (!result) {
                            this.gotoFirstValidationError();
                            return;
                        }

                        this.form.signDate = this.formatDate(this.form.signDate);
                        this.storeSignature();
                    });
                }
            },
            storeSignature() {
                let route = this.$laroute.route("api.contracts.signature.store");

                axios
                    .post(route, this.form)
                    .then(response => {
                        this.$store.dispatch("getEvent", { id: this.form.eventId });
                        this.$store.dispatch("getMe");
                        this.$toasted.show("Agreement signed.");

                        this.$router.push({
                            name: "events-basics",
                            params: { id: this.form.eventId }
                        });
                    })
                    .catch(error => {
                        console.log(error);
                        console.log(error.response);
                    });
            },
            updateSignature() {
                const key = this.$route.params.key
                    ? this.$route.params.key
                    : this.signature.key;
                let route = this.$laroute.route("api.contracts.signature.update", {
                    key: key
                });

                axios
                    .put(route, this.form)
                    .then(response => {
                        this.$toasted.show("Agreement signed.");
                        this.$router.push({ name: "Dashboard" });
                    })
                    .catch(error => {
                        console.log(error);
                        console.log(error.response);
                    });
            },
            getContractByKey() {
                this.isByEmail = true;
                const key = this.$route.params.key;

                let route = this.$laroute.route("api.contract.by.key", {
                    key: key
                });
                axios
                    .get(route)
                    .then(response => {
                        this.$store.dispatch(
                            "setActiveContract",
                            response.data.data
                        );
                        console.log(response);
                    })
                    .catch(error => {
                        this.keyError = true;
                        console.log(error);
                        console.log(error.response);
                    });
            },
            getSignature() {
                let route = this.$laroute.route("api.signature", {
                    event: this.form.eventId
                });

                axios.get(route).then(response => {
                    this.signature = response.data;

                    if (this.signature) {
                        this.hasRecord = true;
                    }
                });
            }
        },
        mounted() {
            if (this.$route.name === "contract-via-email") {
                this.getContractByKey();
            } else {
                this.form.eventId = parseInt(this.$route.params.event);
                this.form.contractId = parseInt(this.$route.params.contract);

                this.getSignature();

                this.$store.dispatch("getEvent", { id: this.form.eventId });
                this.$store.dispatch("getContract", { id: this.form.contractId });
            }
        }
    };
</script>
